import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { STUDENT_NAV } from "@/lib/studentNav";
import { DashboardShell } from "@/components/DashboardShell";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ShoppingCart, Check, ArrowRight, CheckCircle2, Loader2, BadgePercent } from "lucide-react";
import { toast } from "sonner";
import { getPaperCart, getPaperLinePricing, setPaperCart } from "@/lib/paperCart";
import { useAuth } from "@/context/AuthContext";

const NAV = STUDENT_NAV;

export default function Plans() {
  const [plans, setPlans] = useState([]);
  const [papers, setPapers] = useState([]);
  const [cart, setCart] = useState(getPaperCart());
  const [mode, setMode] = useState("test");
  const [buying, setBuying] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([api.get("/plans"), api.get("/paper-store"), api.get("/payments/config")]).then(([planResponse, paperResponse, configResponse]) => {
      setPlans(planResponse.data);
      setPapers(paperResponse.data);
      setMode(configResponse.data.mode);
      const byId = new Map(paperResponse.data.map((paper) => [paper.id, paper]));
      setCart(setPaperCart(getPaperCart()
        .filter((item) => (byId.get(item.paper_id)?.available_sets || 0) > 0)
        .map((item) => ({ ...item, quantity: Math.min(item.quantity, byId.get(item.paper_id).available_sets) }))));
    }).catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)));
  }, []);

  const total = useMemo(
    () => cart.reduce((sum, item) => {
      const paper = papers.find((candidate) => candidate.id === item.paper_id);
      return sum + getPaperLinePricing(paper?.available_set_prices || [], item.quantity).total;
    }, 0),
    [papers, cart]
  );
  const cartQuantity = cart.reduce((sum, item) => sum + item.quantity, 0);
  const products = useMemo(() => {
    const groups = new Map();
    papers.forEach((paper) => {
      if (!groups.has(paper.set_group)) groups.set(paper.set_group, paper);
    });
    return [...groups.values()];
  }, [papers]);

  const toggle = (paper) => {
    const groupIds = new Set(papers.filter((candidate) => candidate.set_group === paper.set_group).map((candidate) => candidate.id));
    const selected = cart.some((item) => groupIds.has(item.paper_id));
    const next = setPaperCart(selected
      ? cart.filter((item) => !groupIds.has(item.paper_id))
      : [...cart.filter((item) => !groupIds.has(item.paper_id)), { paper_id: paper.id, quantity: 1 }]);
    setCart(next);
    toast.success(selected ? "Paper removed from cart" : "Paper added to cart");
  };

  const buyPlan = async (plan) => {
    setBuying(plan.id);
    try {
      const { data } = await api.post("/payments/create-order", { plan_id: plan.id });
      if (data.mode === "test") {
        await api.post("/payments/verify", { plan_id: plan.id, order_id: data.order.id });
        toast.success(`${plan.name} activated successfully`);
        navigate("/student");
        return;
      }
      if (!window.Razorpay) throw new Error("Payment checkout could not be loaded. Please refresh and try again.");
      const checkout = new window.Razorpay({
        key: data.key_id,
        amount: data.order.amount,
        currency: data.order.currency,
        name: "ThetaExams",
        description: `${plan.name} plan`,
        order_id: data.order.id,
        prefill: { name: user?.name || "", email: user?.email || "", contact: user?.phone || "" },
        theme: { color: "#7c3aed" },
        modal: { ondismiss: () => setBuying(null) },
        handler: async (result) => {
          try {
            await api.post("/payments/verify", {
              plan_id: plan.id,
              order_id: result.razorpay_order_id,
              payment_id: result.razorpay_payment_id,
              signature: result.razorpay_signature,
            });
            toast.success(`${plan.name} activated successfully`);
            navigate("/student");
          } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
          } finally {
            setBuying(null);
          }
        },
      });
      checkout.on("payment.failed", (response) => {
        toast.error(response.error?.description || "Payment failed. Please try again.");
        setBuying(null);
      });
      checkout.open();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setBuying(null);
    }
  };

  return (
    <DashboardShell nav={NAV} title="Buy Question Papers">
      <div className="mb-4 flex items-center justify-center gap-2 rounded-md bg-primary px-3 py-2 text-center text-sm font-semibold text-primary-foreground" data-testid="three-set-discount-banner">
        <BadgePercent className="h-4 w-4 shrink-0" aria-hidden="true" /> Purchase 3 sets to get 10% discount
      </div>
      {mode === "test" && (
        <div className="mb-6 rounded-md border border-[hsl(var(--chart-3))]/40 bg-[hsl(var(--chart-3))]/15 px-4 py-3 text-sm" data-testid="test-mode-banner">
          <b>Test mode:</b> payments are simulated and purchases activate immediately.
        </div>
      )}

      {plans.length > 0 && <section className="mb-10" aria-labelledby="purchase-plans-heading">
        <div className="mb-5">
          <p className="overline text-primary">Plans created by admin</p>
          <h2 id="purchase-plans-heading" className="mt-1 font-heading text-2xl font-bold">Choose an evaluation plan</h2>
          <p className="mt-1 text-sm text-muted-foreground">Plans include the subjects, number of sets, and evaluation quota configured by the administrator.</p>
        </div>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
          {plans.map((plan) => (
            <article key={plan.id} data-testid={`buy-plan-${plan.name}`} className="flex flex-col rounded-lg border border-border bg-background p-6">
              <p className="overline text-primary">{plan.plan_type_label}</p>
              <h3 className="mt-1 font-heading text-2xl font-bold">{plan.name}</h3>
              <p className="mt-4 font-heading text-4xl font-black">₹{plan.price_inr}</p>
              <p className="text-sm text-muted-foreground">{plan.sets} set{plan.sets === 1 ? "" : "s"} · {plan.scripts_quota} evaluations</p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {(plan.subjects || []).map((subject) => <span key={subject} className="rounded-full bg-secondary px-2 py-0.5 text-xs">{subject}</span>)}
               </div>
              <ul className="mt-5 flex-1 space-y-2">
                {(plan.features || []).map((feature, index) => <li key={index} className="flex items-start gap-2 text-sm"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> {feature}</li>)}
              </ul>
              <Button className="mt-6 w-full rounded-full" onClick={() => buyPlan(plan)} disabled={buying !== null} data-testid={`purchase-${plan.name}`}>
                {buying === plan.id ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Processing</> : `Buy for ₹${plan.price_inr}`}
              </Button>
            </article>
          ))}
        </div>
      </section>}

      <div className="mb-5 border-t border-border pt-8">
        <p className="overline text-primary">Individual papers</p>
        {/* <h2 className="mt-1 font-heading text-2xl font-bold">Build your own paper bundle</h2>
        {(user?.board || user?.medium) && (
          <p className="mt-1 text-sm text-muted-foreground">
            Showing question papers for {user?.board || "your board"} in {user?.medium || "your selected medium"}.
          </p>
        )} */}
      </div>
      <div className="mb-6 flex flex-col gap-4 rounded-lg border border-border bg-background p-5 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="font-heading text-xl font-bold">Choose the papers you want</p>
          <p className="mt-1 text-sm text-muted-foreground">Choose papers here, then set the number of sets you need at checkout.</p>
        </div>
        <Link to="/student/checkout" className="hidden sm:block">
          <Button className="w-full gap-2 rounded-full sm:w-auto" disabled={cart.length === 0} data-testid="go-to-checkout">
            <ShoppingCart className="h-4 w-4" /> Cart ({cartQuantity}) · ₹{total} <ArrowRight className="h-4 w-4" />
          </Button>
        </Link>
      </div>

      <div className={`grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 ${cartQuantity > 0 ? "pb-24 sm:pb-0" : ""}`}>
        {products.map((paper) => {
          const groupIds = new Set(papers.filter((candidate) => candidate.set_group === paper.set_group).map((candidate) => candidate.id));
          const selected = cart.some((item) => groupIds.has(item.paper_id));
          return (
            <article key={paper.set_group} className={`flex flex-col rounded-lg border bg-background p-6 transition ${selected ? "border-primary ring-1 ring-primary" : "border-border"}`} data-testid={`paper-product-${paper.id}`}>
              <div className="flex items-start justify-between gap-3">
                <span className="overline text-primary">{paper.subject}</span>
                <span className="rounded-full bg-secondary px-2 py-1 text-xs font-medium">{paper.available_sets} set{paper.available_sets === 1 ? "" : "s"} available</span>
              </div>
              <h2 className="mt-2 font-heading text-xl font-bold">{paper.subject} Question Papers</h2>
              <p className="mt-2 text-sm text-muted-foreground">{paper.board || "All boards"} · {paper.grade || "All classes"} · {paper.language || "English"} · {paper.total_marks || 0} marks</p>
              {/* <p className="mt-1 text-xs text-muted-foreground">Feedback will be provided in your {user?.medium || "selected"} medium.</p> */}
              {/* <p className="mt-1 text-xs text-muted-foreground">{paper.total_sets} distinct set{paper.total_sets === 1 ? "" : "s"} uploaded by admin{paper.purchased_sets ? ` · ${paper.purchased_sets} purchased` : ""}</p> */}
              <div className="mt-6 flex flex-1 items-end justify-between gap-4">
                <p className="font-heading text-3xl font-black">₹{Math.min(...(paper.available_set_prices.length ? paper.available_set_prices : [paper.price_inr]))}</p>
                <Button variant={selected ? "outline" : "default"} className="gap-2 rounded-full" disabled={!selected && paper.available_sets <= 0} onClick={() => toggle(paper)} data-testid={`add-paper-${paper.id}`}>
                  {selected ? <><Check className="h-4 w-4" /> Added</> : paper.available_sets <= 0 ? "No new sets" : <><ShoppingCart className="h-4 w-4" /> Add to cart</>}
                </Button>
              </div>
            </article>
          );
        })}
        {papers.length === 0 && <p className="text-sm text-muted-foreground">No papers are available for purchase yet.</p>}
      </div>

      {/* Escape DashboardShell's transformed fade-up container so fixed positioning uses the viewport. */}
      {cartQuantity > 0 && createPortal(
        <div className="pointer-events-none fixed inset-x-0 bottom-[max(1rem,env(safe-area-inset-bottom))] z-[25] flex justify-center px-4 sm:hidden">
          <Link
            to="/student/checkout"
            className="pointer-events-auto flex h-16 w-[min(260px,calc(100vw-2rem))] touch-pan-y items-center rounded-full bg-blue-600 p-2 text-white shadow-[0_8px_24px_rgba(0,0,0,0.32)] transition-transform active:scale-[0.98]"
            aria-label={`View cart with ${cartQuantity} ${cartQuantity === 1 ? "item" : "items"}`}
            data-testid="mobile-view-cart"
          >
            <span className="flex h-12 w-[72px] shrink-0 items-center justify-center rounded-full bg-white px-2 shadow-sm" aria-hidden="true">
              {cart.slice(0, 2).map((item, index) => {
                const paper = papers.find((candidate) => candidate.id === item.paper_id);
                return (
                  <span
                    key={item.paper_id}
                    className={`flex h-9 w-9 items-center justify-center rounded-full border-2 border-white bg-blue-50 font-heading text-sm font-black text-blue-600 shadow-sm ${index > 0 ? "-ml-3" : ""}`}
                  >
                    {(paper?.subject || "P").charAt(0).toUpperCase()}
                  </span>
                );
              })}
            </span>
            <span className="min-w-0 flex-1 px-4 leading-tight">
              <span className="block font-heading text-[17px] font-bold">View cart</span>
              <span className="mt-1 block text-[13px] font-medium text-white/90">
                {cartQuantity} {cartQuantity === 1 ? "item" : "items"}
              </span>
            </span>
            <ArrowRight className="mr-3 h-6 w-6 shrink-0 stroke-[3]" aria-hidden="true" />
          </Link>
        </div>,
        document.body
     )}
    </DashboardShell>
  );
}
